import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  X, Trash2, Plus, Calendar, User, AlertTriangle,
  TrendingDown, CheckCircle2, History, DollarSign, Filter
} from 'lucide-react';
import { useItems, useWastes, useCreateWaste } from '../hooks/useInventory';

const WASTE_REASONS = [
  { label: 'Quemado / Error cocción', color: 'bg-red-500/10 text-red-600 border-red-500/30' },
  { label: 'Vencimiento / Caducidad', color: 'bg-amber-500/10 text-amber-600 border-amber-500/30' },
  { label: 'Rotura / Caída', color: 'bg-orange-500/10 text-orange-600 border-orange-500/30' },
  { label: 'Deterioro / Calidad', color: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30' },
  { label: 'Devolución de cliente', color: 'bg-purple-500/10 text-purple-600 border-purple-500/30' },
  { label: 'Degustación / Muestra', color: 'bg-blue-500/10 text-blue-600 border-blue-500/30' },
  { label: 'Otro', color: 'bg-gray-500/10 text-gray-600 border-gray-500/30' },
];

interface WasteLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultItemId?: number | string | null;
}

export const WasteLogModal: React.FC<WasteLogModalProps> = ({ isOpen, onClose, defaultItemId }) => {
  const [activeTab, setActiveTab] = useState<'RECORD' | 'LOGS'>('RECORD');
  const [selectedItemId, setSelectedItemId] = useState<string>(defaultItemId ? String(defaultItemId) : '');
  const [qty, setQty] = useState<string>('');
  const [reason, setReason] = useState<string>('Quemado / Error cocción');
  const [responsibleName, setResponsibleName] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const { data: itemsPage } = useItems({ limit: 1000 });
  const { data: wastesData, isLoading: loadingWastes } = useWastes();
  const createWasteMut = useCreateWaste();

  if (!isOpen) return null;

  const items = itemsPage?.data || [];
  const selectedItem = items.find((i) => String(i.id) === selectedItemId);
  const estimatedCost = selectedItem && Number(qty) > 0 ? Number(qty) * Number(selectedItem.cost || 0) : 0;

  const handleSaveWaste = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItemId) {
      toast.warning('Selecciona el insumo que tuvo la merma.');
      return;
    }
    const numQty = parseFloat(qty);
    if (!numQty || numQty <= 0) {
      toast.warning('Ingresa una cantidad válida mayor a cero.');
      return;
    }

    createWasteMut.mutate(
      {
        inventory_item_id: Number(selectedItemId),
        qty: numQty,
        unit: selectedItem?.unit,
        reason,
        responsible_name: responsibleName.trim() || undefined,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: (res) => {
          toast.success(res.message || 'Merma registrada y stock descontado.');
          setQty('');
          setNotes('');
          setActiveTab('LOGS');
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.message || 'Error al registrar la merma.');
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-3xl max-h-[90vh] flex flex-col rounded-3xl bg-surface border border-divider shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-divider bg-surface-muted/30">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-red-500/10 text-red-500">
              <Trash2 size={26} />
            </div>
            <div>
              <h3 className="text-xl font-extrabold text-[color:var(--color-text)]">
                Control de Mermas y Desperdicios
              </h3>
              <p className="text-xs text-muted mt-0.5">
                Registra caídas, quemados o vencimientos con costeo automático de pérdidas ($).
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-muted hover:bg-surface-muted hover:text-[color:var(--color-text)] transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab Selector */}
        <div className="flex items-center gap-2 px-6 pt-4 border-b border-divider bg-surface">
          <button
            onClick={() => setActiveTab('RECORD')}
            className={`pb-3 px-3 text-xs font-bold border-b-2 transition flex items-center gap-2 ${
              activeTab === 'RECORD'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted hover:text-[color:var(--color-text)]'
            }`}
          >
            <Plus size={15} /> Registrar Merma
          </button>
          <button
            onClick={() => setActiveTab('LOGS')}
            className={`pb-3 px-3 text-xs font-bold border-b-2 transition flex items-center gap-2 ${
              activeTab === 'LOGS'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted hover:text-[color:var(--color-text)]'
            }`}
          >
            <History size={15} /> Historial y Pérdidas ({wastesData?.total_events || 0})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'RECORD' ? (
            <form onSubmit={handleSaveWaste} className="space-y-5 max-w-xl mx-auto">
              {/* Seleccionar Insumo */}
              <div>
                <label className="block text-xs font-semibold text-muted mb-1">
                  Insumo afectado *
                </label>
                <select
                  value={selectedItemId}
                  onChange={(e) => setSelectedItemId(e.target.value)}
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                >
                  <option value="">-- Selecciona un insumo --</option>
                  {items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} ({item.unit}) - Costo: ${Number(item.cost || 0).toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>

              {/* Cantidad y Unidad */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-muted mb-1">
                    Cantidad desperdiciada *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="any"
                      min="0.0001"
                      value={qty}
                      onChange={(e) => setQty(e.target.value)}
                      placeholder="0.00"
                      required
                      className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary font-mono font-bold"
                    />
                    <span className="absolute right-3 top-2 text-xs text-muted">
                      {selectedItem?.unit || 'und'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted mb-1">
                    Pérdida Monetaria Estimada
                  </label>
                  <div className="rounded-xl border border-red-500/20 bg-red-500/5 px-3 py-2 text-xs font-bold text-red-500 font-mono flex items-center gap-1">
                    <DollarSign size={14} />
                    {estimatedCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              {/* Motivo de la merma con botones rápidos */}
              <div>
                <label className="block text-xs font-semibold text-muted mb-2">
                  Motivo del desperdicio *
                </label>
                <div className="flex flex-wrap gap-2">
                  {WASTE_REASONS.map((r) => (
                    <button
                      key={r.label}
                      type="button"
                      onClick={() => setReason(r.label)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition ${
                        reason === r.label
                          ? 'ring-2 ring-primary border-primary bg-primary text-white'
                          : `${r.color} hover:opacity-80`
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Responsable */}
              <div>
                <label className="block text-xs font-semibold text-muted mb-1">
                  Responsable / Cocinero / Barman (opcional)
                </label>
                <input
                  type="text"
                  value={responsibleName}
                  onChange={(e) => setResponsibleName(e.target.value)}
                  placeholder="Ej: Juan Pérez (Cocina caliente)"
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Notas */}
              <div>
                <label className="block text-xs font-semibold text-muted mb-1">
                  Detalles adicionales (opcional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ej: Se cayó la bandeja al sacar del horno."
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary h-20"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={createWasteMut.isPending}
                  className="w-full py-3 rounded-xl bg-red-500 text-white font-bold text-xs shadow-md hover:bg-red-600 transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Trash2 size={16} />
                  {createWasteMut.isPending ? 'Guardando merma...' : 'Registrar Merma y Descontar Stock'}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              {/* KPIs de pérdidas */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-2xl bg-red-500/5 border border-red-500/20">
                  <span className="text-[11px] text-muted uppercase font-bold tracking-wider">Costo Total Perdido</span>
                  <div className="mt-1 text-2xl font-extrabold text-red-500">
                    ${(wastesData?.total_loss_cost || 0).toLocaleString()}
                  </div>
                </div>
                <div className="p-4 rounded-2xl bg-surface-muted/30 border border-divider">
                  <span className="text-[11px] text-muted uppercase font-bold tracking-wider">Total Incidentes</span>
                  <div className="mt-1 text-2xl font-extrabold text-[color:var(--color-text)]">
                    {wastesData?.total_events || 0} registros
                  </div>
                </div>
              </div>

              {/* Listado */}
              {loadingWastes ? (
                <div className="py-12 text-center text-muted text-xs">Cargando registros...</div>
              ) : !wastesData?.data?.length ? (
                <div className="py-12 text-center text-muted text-xs">No hay mermas registradas.</div>
              ) : (
                <div className="divide-y divide-divider border border-divider rounded-2xl overflow-hidden bg-surface">
                  {wastesData.data.map((w) => (
                    <div key={w.id} className="p-4 hover:bg-surface-muted/30 transition flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <strong className="text-[color:var(--color-text)] font-bold text-sm">
                            {w.item?.name || 'Insumo'}
                          </strong>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/10 text-red-600 border border-red-500/20">
                            {w.reason}
                          </span>
                        </div>
                        <div className="text-muted text-[11px] flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} /> {new Date(w.wasted_at).toLocaleString()}
                          </span>
                          {w.responsible_name && (
                            <span className="flex items-center gap-1">
                              <User size={12} /> {w.responsible_name}
                            </span>
                          )}
                        </div>
                        {w.notes && <p className="text-muted italic text-[11px]">"{w.notes}"</p>}
                      </div>

                      <div className="text-right">
                        <span className="text-muted block text-[10px] uppercase font-semibold">
                          Cantidad: {w.qty} {w.unit}
                        </span>
                        <span className="font-mono font-bold text-sm text-red-500">
                          -${w.total_cost.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
